import * as Phaser from "phaser-ce"
import { Align } from "../../common/TextUtils"
import { ActivityKey } from "../../shared/models/ActivityKey"
import { GameState, TransitionState } from "../GameState"
import { Buttons, ButtonSize } from "../generic-sprites/Buttons"
import { GenericAssets, loadGenericAssets } from "../generic-sprites/GenericAssets"
import { PhaserText } from "../utils/PhaserText"
import { PhaserUtils } from "../utils/PhaserUtils"
import { Scale } from "../utils/Scale"
import { Sprites } from "../utils/Sprites"
import { WorldRepo } from "./WorldRepo"

export class WorldState extends Phaser.State {

    private static readonly SECOND_COLUMN_X = 0.4
    private yGap: number
    private currentY: number
    private currentY2: number
    private itemPadding = Scale.height() * 0.02
    private buttonScale = 0.9

    preload() {
        loadGenericAssets(this.game)
    }

    async create() {
        await PhaserUtils.sharedCreateFunctions(this.game)

        this.yGap = 29 * Scale.scaleAmount()

        this.currentY = this.yGap
        this.currentY2 = this.yGap

        this.addToggleProdButton()

        this.addClearCacheButton()
        this.addEcig101DestinationButtons()

        this.addSpace2ndColumn()
        this.addSpace2ndColumn()
        this.addSpace2ndColumn()
        this.addSpace2ndColumn()
        this.addSpace2ndColumn()
        this.addSpace2ndColumn()
        this.addSpace2ndColumn()
        this.addSpace2ndColumn()
        this.addSpace2ndColumn()

        this.addLink2ndColumn("Super Hero Intro", TransitionState.SUPER_HERO_INTRO)
        this.addLink2ndColumn("Super Hero", ActivityKey.SUPER_HERO)
        this.addLink2ndColumn("Super Hero Exit", TransitionState.SUPER_HERO_EXIT)
        this.addSpace2ndColumn()

        this.addLink2ndColumn("Second Hand Intro", TransitionState.SECOND_HAND_INTRO)
        this.addLink2ndColumn("Second Hand", ActivityKey.SECOND_HAND)
        this.addLink2ndColumn("Second Hand Short Term Effects", TransitionState.SHORT_TERM)
        this.addLink2ndColumn("Second Hand Long Term Effects", TransitionState.LONG_TERM)
        this.addLink2ndColumn("Second Hand Short Term Vapor Effects", TransitionState.EFFECTS_OF_VAPOR)
        this.addLink2ndColumn("Second Hand Primary vs. Secondary Effects", TransitionState.SECOND_VS_DIRECT)
        this.addLink2ndColumn("Second Hand Exit", TransitionState.SECOND_HAND_EXIT)
        this.addSpace2ndColumn()

        this.addLink2ndColumn("Reality Check Intro", TransitionState.REALITY_CHECK_INTRO)
        this.addLink2ndColumn("Reality Check", ActivityKey.REALITY_CHECK)
        this.addLink2ndColumn("Reality Check Exit", TransitionState.REALITY_CHECK_EXIT)
        this.addSpace2ndColumn()

        this.addLink2ndColumn("Map", TransitionState.MAP)
        this.addSpace2ndColumn()

        this.addLink("Addiction 101 Intro", TransitionState.ADDICTION_101_INTRO)
        this.addLink("Addiction 101", ActivityKey.ADDICTION_MAIN)
        this.addLink("Addiction 101 -- Mental", TransitionState.ADDICTION_MENTAL)
        this.addLink("Addiction 101 -- Physical", TransitionState.ADDICTION_PHYSICAL)
        this.addLink("Addiction 101 -- Nicotine", TransitionState.ADDICTION_NICOTINE)
        this.addLink("Addiction 101 -- Cravings", TransitionState.ADDICTION_CRAVINGS_AND_WITHDRAWAL)
        this.addLink("Addiction 101 Exit", TransitionState.ADDICTION_101_EXIT)
        this.addSpace()

        this.addLink("Addiction Pong Intro", TransitionState.ADDICTION_PONG_INTRO)
        this.addLink("Addiction Pong", ActivityKey.ADDICTION_PONG)
        this.addLink("Addiction Pong Exit", TransitionState.ADDICTION_PONG_EXIT)
        this.addSpace()

        this.addLink("E-cig 101 Intro", TransitionState.ECIG_101_INTRO)
        this.addLink("E-cig 101", ActivityKey.ECIG_101)
        this.addLink("E-cig 101 Exit", TransitionState.ECIG_101_EXIT)
        this.addSpace()

        this.addLink("E-cig Tour Intro", TransitionState.TOUR_INTRO)
        this.addLink("E-cig Tour", TransitionState.ECIG_TOUR)
        this.addLink("E-cig Tour Exit", TransitionState.TOUR_EXIT)
        this.addSpace()

        this.addLink("E-cig Tour Intro", TransitionState.TOUR_INTRO)
        this.addSpace()

    }

    addSpace = () => {
        this.currentY += this.yGap / 3
    }

    addSpace2ndColumn = () => {
        this.currentY2 += this.yGap / 3
    }

    addLink = (text: string, startState: GameState) => {
        this.addLinkShared(text, startState, this.itemPadding, this.currentY)
        this.currentY += this.yGap
    }

    addLink2ndColumn = (text: string, startState: GameState) => {
        this.addLinkShared(text, startState, Scale.width() * WorldState.SECOND_COLUMN_X, this.currentY2)
        this.currentY2 += this.yGap
    }

    addLinkShared = (text: string, startState: GameState, x: number, y: number) => {
        const link = PhaserText.create(this.game, text, {x, y, fontPx: 20})
        Buttons.makeSpriteButton(link)
        link.events.onInputDown.add(() => {
            this.game.state.start(startState)
        }, this)
    }

    addClearCacheButton = () => {
        const button = Buttons.create(this.game, "Clear Cache", () => {
            localStorage.clear()
            window.location.reload()
        }, {x: Scale.x(WorldState.SECOND_COLUMN_X), y: this.currentY, buttonSize: ButtonSize.WIDE})

        button.button.x += button.button.width + this.itemPadding
        button.center()
    }

    addEcig101DestinationButtons = () => {
        const destinations = [TransitionState.TOUR_INTRO, TransitionState.ADDICTION_101_INTRO, TransitionState.SECOND_HAND_INTRO,
            TransitionState.REALITY_CHECK_INTRO]
        let currentX = Scale.width() * WorldState.SECOND_COLUMN_X
        const buttons: Array<Phaser.Button> = []
        destinations.forEach((destination) => {
            const button = this.game.add.button(currentX, 0, GenericAssets.TOGGLE_BUTTON, () => {
                WorldRepo.setEcig101Destination(destination)
                buttons.forEach((clearButton) => {
                    clearButton.frame = 0
                })
                button.frame = 2
            })
            Scale.scaleSprite(button, this.buttonScale)
            button.y = this.currentY + button.height + this.itemPadding
            currentX += button.width + this.itemPadding

            const currentDestination = WorldRepo.getEcig101Destination()
            if (currentDestination === destination) {
                button.frame = 2
            }

            Sprites.centerTextInSprite(button, PhaserText.create(this.game, destination, {fontPx: 10}))
            buttons.push(button)
        })

    }

    addToggleProdButton = () => {
        const button = this.game.add.button(0, 0,
            GenericAssets.TOGGLE_BUTTON, () => {
                WorldRepo.toggleDebugMode()
                buttonText.text = "Debug: " + WorldRepo.isDebug()
                window.location.reload()
            }, this, 1, 0, 2)
        Scale.scaleSprite(button, this.buttonScale)
        const buttonText = PhaserText.create(this.game, "Debug: " + WorldRepo.isDebug(),
            {fontPx: 16, align: Align.CENTER})
        button.y = Scale.height() - button.height

        Sprites.centerTextInSprite(button, buttonText)
    }

}
